import { Decorative, TopShop } from "@faire/design-tokens";
import { ICollectionPromo } from "@faire/web-api/indigofair/data/ICollectionPromo";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { IRetailerProductBasedCollectionResponse } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionResponse";
import { Avatar, AvatarSize } from "@faire/web/slate/deprecated/Avatar";
import { TrophyIcon } from "@faire/web/slate/icons/Trophy";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";

import { SERRANO } from "@faire/retailer/consts/colors";
import { bpsToPercent } from "@faire/retailer/lib/bpsToDecimalAndPercent";
import {
  useAssignSettingUpdateSaleColorCopy,
  useSettingUpdateSaleColorCopy,
} from "@faire/retailer/settings/useSettingUpdateSaleColorCopy";

import IRetailerProductBasedCollection = IRetailerProductBasedCollectionResponse.IRetailerProductBasedCollection;

interface ICollectionHeaderProps {
  collection: IRetailerProductBasedCollectionResponse.IRetailerProductBasedCollection;
  collectionPromo?: ICollectionPromo;
}

interface CollectionHeaderLogoProps {
  size: AvatarSize;
  image?: IImage;
  isTopShop?: boolean;
}

const CollectionHeaderLogo: React.FC<CollectionHeaderLogoProps> = ({
  size,
  image,
  isTopShop = false,
}) => {
  // TODO: Address the actual image used here and force it to pad instead of crop. @Julian-Lannigan to follow up with Slate team to maybe figure out a better approach.
  // https://faire-wholesale.slack.com/archives/C01BU80K6GL/p1677187664529379
  const taggedImage = IImage.build({
    ...image,
    tags: ["cloudinary_crop_pad"],
  });

  if (isTopShop) {
    return (
      <div>
        <Avatar
          size={size}
          image={taggedImage}
          borderColor={TopShop.shade.green}
          iconData={{
            position: "bottomRight",
            icon: (
              <TrophyIcon
                color={TopShop.icon.green}
                fill={TopShop.icon.green}
                active
                width={size === "x-small" ? "16px" : "30px"}
                height={size === "x-small" ? "16px" : "30px"}
              />
            ),
          }}
        />
      </div>
    );
  } else {
    return (
      <div>
        <Avatar size={size} image={taggedImage} />
      </div>
    );
  }
};

export const CollectionHeader: React.FC<ICollectionHeaderProps> = ({
  collection,
  collectionPromo,
}) => {
  const { isMobile, isTabletAndAbove } = useViewport();
  return (
    <ColToRow>
      <HeaderDetails data-test-id="collection-header-details">
        {isMobile ? (
          <MobileCol data-test-id="collection-mobile-header" gap="2x">
            <CollectionHeaderLogo
              size="x-small"
              image={collection?.brand_logo_image}
              isTopShop={collection?.brand_is_top_shop}
            />
            <HeaderTag
              collection={collection}
              collectionPromo={collectionPromo}
            />
          </MobileCol>
        ) : (
          <HeaderTag
            collection={collection}
            collectionPromo={collectionPromo}
          />
        )}
        <Typography
          data-test-id="collection-name"
          variant="displayLSerifRegular"
        >
          {collection?.name}
        </Typography>
        {collection?.description ? (
          <>
            <Spacer height="2x" />
            <Typography data-test-id="collection-description">
              {collection.description}
            </Typography>
          </>
        ) : null}
      </HeaderDetails>
      {isTabletAndAbove ? (
        <Row justify="right" data-test-id="collection-tabletAndAbove-avatar">
          <CollectionHeaderLogo
            size="large"
            image={collection?.brand_logo_image}
            isTopShop={collection?.brand_is_top_shop}
          />
        </Row>
      ) : null}
    </ColToRow>
  );
};

const HeaderTag: React.FC<{
  collection: IRetailerProductBasedCollection;
  collectionPromo?: ICollectionPromo;
}> = ({ collection, collectionPromo }) => {
  const shouldShowDiscountTag = collectionPromo?.discount_bps;
  const assignUpdateSaleColor = useAssignSettingUpdateSaleColorCopy();
  const isSettingUpdateSaleColor = useSettingUpdateSaleColorCopy();
  const shouldShowFreeDisplayTag =
    collection.active_display_promo?.type === "FREE_DISPLAY";

  useEffect(() => {
    if (shouldShowDiscountTag) {
      assignUpdateSaleColor();
    }
  }, [assignUpdateSaleColor, shouldShowDiscountTag]);

  return (
    <Row align="center">
      <Typography variant="paragraphSansMedium">
        <StrictLocalMsg
          defaultMessage="Collection"
          description={{
            text: "Tag name. A collection of products.",
          }}
        />
      </Typography>
      {shouldShowDiscountTag ? (
        <>
          <Spacer width="1x" />
          <DiscountTag $isUpdateSaleColor={isSettingUpdateSaleColor}>
            <Typography
              variant="paragraphSansMedium"
              color={
                isSettingUpdateSaleColor ? SERRANO : Decorative.shade.orange
              }
            >
              {shouldShowFreeDisplayTag ? (
                <StrictLocalMsg
                  defaultMessage="Free display + {discountPercentageNumber}% off"
                  description={{
                    text: "This collection offers a free display product and products in this collection enjoy a discount",
                  }}
                  values={{
                    discountPercentageNumber: bpsToPercent(
                      collectionPromo?.discount_bps ?? 0
                    ),
                  }}
                />
              ) : (
                <StrictLocalMsg
                  defaultMessage="{discountPercentageNumber}% off"
                  description={{
                    text: "The product in this collection enjoy a discount",
                  }}
                  values={{
                    discountPercentageNumber: bpsToPercent(
                      collectionPromo?.discount_bps ?? 0
                    ),
                  }}
                />
              )}
            </Typography>
          </DiscountTag>
        </>
      ) : null}
    </Row>
  );
};

const DiscountTag = styled.div<{ $isUpdateSaleColor?: boolean }>`
  ${({ $isUpdateSaleColor }) =>
    !$isUpdateSaleColor &&
    css`
      background-color: ${Decorative.tint.orange};
    `}
  padding: ${getSpacing(4, 6)};
`;

const ColToRow = styled(Flex).attrs({
  align: { mobileAndAbove: "center", tabletAndAbove: "stretch" },
  justify: { mobileAndAbove: "normal", tabletAndAbove: "space-between" },
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})`
  width: 100%;
`;

const MobileCol = styled(Row).attrs({ align: "center" })`
  margin-bottom: ${getSpacing("1x")};
`;

const HeaderDetails = styled(Column).attrs({
  align: "flex-start",
  justify: "flex-start",
})`
  width: 100%;
  z-index: 1;

  ${responsiveMediaQueries.desktopAndAbove} {
    flex-basis: 60%;
  }

  ${responsiveMediaQueries.tablet} {
    flex-basis: 50%;
  }

  ${responsiveMediaQueries.mobile} {
    flex-basis: 100%;
    padding: 32px 16px 0;
  }
`;
